import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import PrintableLabel from './printableLabel';

const LabelCustomizationPage = ({ users, weekDay, mealOption, onClose }) => {
  // Load initial state from localStorage or use default values
  const [dimensions, setDimensions] = useState(() => {
    const saved = localStorage.getItem('labelDimensions');
    return saved ? JSON.parse(saved) : { width: '48mm', height: '23mm' };
  });

  const [frameDimensions, setFrameDimensions] = useState(() => {
    const saved = localStorage.getItem('frameDimensions');
    return saved ? JSON.parse(saved) : { width: '100mm', height: '100mm' };
  });

  const [contentOptions, setContentOptions] = useState(() => {
    const saved = localStorage.getItem('contentOptions');
    return saved ? JSON.parse(saved) : { showServingdag: true, showAllergies: true };
  });

  const [textStyles, setTextStyles] = useState(() => {
    const saved = localStorage.getItem('textStyles');
    return saved
      ? JSON.parse(saved)
      : {
          title: { fontSize: 12, isBold: true }, // username
          date: { fontSize: 10, isBold: false }, // Servingdag
          name: { fontSize: 11, isBold: true }, // person_name
          text: { fontSize: 10, isBold: false }, // allergies
        };
  });

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const handleShowSettingsModal = () => setShowSettingsModal(true);
  const handleCloseSettingsModal = () => setShowSettingsModal(false);

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => onClose(), // Close modal after printing
    onPrintCancel: () => onClose(), // Close modal if print is canceled
  });

  // Save dimensions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('labelDimensions', JSON.stringify(dimensions));
  }, [dimensions]);

  // Save frameDimensions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('frameDimensions', JSON.stringify(frameDimensions));
  }, [frameDimensions]);

  // Save contentOptions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('contentOptions', JSON.stringify(contentOptions));
  }, [contentOptions]);

  // Save textStyles to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('textStyles', JSON.stringify(textStyles));
  }, [textStyles]);

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFrameDimensionChange = (e) => {
    const { name, value } = e.target;
    setFrameDimensions(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentOptionChange = (e) => {
    const { name, checked } = e.target;
    setContentOptions(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTextStyleChange = (field, property, value) => {
    setTextStyles(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: property === 'fontSize' ? Number(value) : value,
      },
    }));
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <span className='me-2 font-size-s'>Mallen Inställningar</span>
        <button
          className="cursor stats_card"
          onClick={handleShowSettingsModal}
          style={{ background: 'none', border: 'none', padding: '5px' }}
        >
          <i className="fa-solid fa-gear glow"></i>
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h6>Förhandsgranskning</h6>
        <PrintableLabel
          users={users}
          weekDay={weekDay}
          contentOptions={contentOptions}
          frameDimensions={frameDimensions}
          textStyles={textStyles}
          mealOption={mealOption}
        />
      </div>
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <PrintableLabel
            users={users}
            weekDay={weekDay}
            contentOptions={contentOptions}
            frameDimensions={frameDimensions}
            textStyles={textStyles}
            mealOption={mealOption}
          />
        </div>
      </div>
      <button
        className="cursor stats_card mr-2 mb-2"
        onClick={handlePrint}
      >
        Skriv ut
      </button>

      {/* Settings Modal */}
      <Modal show={showSettingsModal} onHide={handleCloseSettingsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Inställningar för Etiketter</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'white' }}>
          <div style={{ marginBottom: '20px' }}>
            <h4>Etikettstorlek</h4>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label htmlFor="width-input">Bredd</label>
                <input
                  id="width-input"
                  type="text"
                  name="width"
                  value={dimensions.width}
                  onChange={handleDimensionChange}
                  placeholder="e.g., 48mm"
                  style={{ marginLeft: '5px', width: '100px' }}
                />
              </div>
              <div>
                <label htmlFor="height-input">Höjd</label>
                <input
                  id="height-input"
                  type="text"
                  name="height"
                  value={dimensions.height}
                  onChange={handleDimensionChange}
                  placeholder="e.g., 23mm"
                  style={{ marginLeft: '5px', width: '100px' }}
                />
              </div>
            </div>
          </div>
          <hr></hr>
          <div style={{ marginBottom: '20px' }}>
            <h4>Ramstorlek</h4>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label htmlFor="frame-width-input">Bredd</label>
                <input
                  id="frame-width-input"
                  type="text"
                  name="width"
                  value={frameDimensions.width}
                  onChange={handleFrameDimensionChange}
                  placeholder="e.g., 100mm"
                  style={{ marginLeft: '5px', width: '100px' }}
                />
              </div>
              <div>
                <label htmlFor="frame-height-input">Höjd</label>
                <input
                  id="frame-height-input"
                  type="text"
                  name="height"
                  value={frameDimensions.height}
                  onChange={handleFrameDimensionChange}
                  placeholder="e.g., 100mm"
                  style={{ marginLeft: '5px', width: '100px' }}
                />
              </div>
            </div>
          </div>
          <hr></hr>
          <div style={{ marginBottom: '20px' }}>
            <h4>Textstil</h4>
            <div style={{ marginBottom: '10px' }}>
              <label>Användarnamn (fontstorlek: {textStyles.title.fontSize}px)</label>
              <input
                type="range"
                min="8"
                max="20"
                value={textStyles.title.fontSize}
                onChange={(e) => handleTextStyleChange('title', 'fontSize', e.target.value)}
                style={{ width: '200px', marginLeft: '10px' }}
              />
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={textStyles.title.isBold}
                  onChange={(e) => handleTextStyleChange('title', 'isBold', e.target.checked)}
                />
                Fetstil
              </label>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>Servingdag (fontstorlek: {textStyles.date.fontSize}px)</label>
              <input
                type="range"
                min="8"
                max="20"
                value={textStyles.date.fontSize}
                onChange={(e) => handleTextStyleChange('date', 'fontSize', e.target.value)}
                style={{ width: '200px', marginLeft: '10px' }}
              />
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={textStyles.date.isBold}
                  onChange={(e) => handleTextStyleChange('date', 'isBold', e.target.checked)}
                />
                Fetstil
              </label>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>Namn (fontstorlek: {textStyles.name.fontSize}px)</label>
              <input
                type="range"
                min="8"
                max="20"
                value={textStyles.name.fontSize}
                onChange={(e) => handleTextStyleChange('name', 'fontSize', e.target.value)}
                style={{ width: '200px', marginLeft: '10px' }}
              />
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={textStyles.name.isBold}
                  onChange={(e) => handleTextStyleChange('name', 'isBold', e.target.checked)}
                />
                Fetstil
              </label>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>Allergier (fontstorlek: {textStyles.text.fontSize}px)</label>
              <input
                type="range"
                min="8"
                max="20"
                value={textStyles.text.fontSize}
                onChange={(e) => handleTextStyleChange('text', 'fontSize', e.target.value)}
                style={{ width: '200px', marginLeft: '10px' }}
              />
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={textStyles.text.isBold}
                  onChange={(e) => handleTextStyleChange('text', 'isBold', e.target.checked)}
                />
                Fetstil
              </label>
            </div>
          </div>
          <hr></hr>
          <div style={{ marginBottom: '20px' }}>
            <h4>Innehåll</h4>
            <div>
              <label>
                <input
                  type="checkbox"
                  name="showServingdag"
                  checked={contentOptions.showServingdag}
                  onChange={handleContentOptionChange}
                />
                Visa Servingdag
              </label>
            </div>
            <div>
              <label>
                <input
                  type="checkbox"
                  name="showAllergies"
                  checked={contentOptions.showAllergies}
                  onChange={handleContentOptionChange}
                />
                Visa Allergi mot
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="cursor stats_card"
            onClick={handleCloseSettingsModal}
          >
            Stäng
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabelCustomizationPage;